import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Appointment, AppointmentStatus } from '../../types';
import { Box, Divider, Stack, Button, TextField, Grid2 } from '@mui/material';
import { convertISOToTime } from '../../services/time';
import { cancelAppointment } from '../../services/apiService';
import { useAlert } from '../alertProvider';
import {LabeledWithAsterisk, toTitleCase } from '../../utils/commonUtils';

type ICancel = {
  data: Appointment | undefined;
  onClose: (value: boolean) => void;
};

const Cancel: React.FC<ICancel> = ({ data, onClose }) => {
  const { addAlert } = useAlert();
  const { t: tAppointmentForm } = useTranslation('appointment_form');
  const { t: tCalendar } = useTranslation('calendar');

  const [remark, setRemark] = useState<string>('');
  const [cancelationReason, setCancelationReason] = useState<string>('');
  const [isFieldDirty, setIsFieldDirty] = useState<boolean>(false);

  const handleChange = (value: string, name: string) => {
    setIsFieldDirty(false);
    if (name === 'cancellationreason') {
      setCancelationReason(value);
    } else if (name === 'remark') {
      setRemark(value);
    }
  };

  const handleCancel = () => {
    const params = {
      remark: remark,
      status: AppointmentStatus.CANCELLED,
      cancel_or_reschedule_reason: cancelationReason,
    };

    if (!params.cancel_or_reschedule_reason.length) {
      addAlert(tAppointmentForm('kindlyFillRequiredFields'), 'error');
      setIsFieldDirty(true);
    } else {
      cancelAppointment(data?.id ?? 0, params)
        .then(() => {
          addAlert(tAppointmentForm('appointmentCancelledSuccess'), 'success');
          onClose(false);
        })
        .catch((err) => {
          err?.response?.data?.errors?.map((err: { detail: string }) => {
            return addAlert(err.detail, 'error');
          });
        });
    }
  };

  const renderDetail = (
    label: string,
    value: string | undefined,
    isDisabled: boolean,
    type?: string,
    width?: number,
    rows?: number,
    fieldKey?: string,
  ) => {
    let CustomTextField;
    if (fieldKey === 'cancellationreason') {
      CustomTextField = (
        <TextField
          label={LabeledWithAsterisk(label, true)}
          value={cancelationReason}
          slotProps={{ inputLabel: { sx: { color: '#333' } } }}
          onChange={(e) => handleChange(e.target.value, 'cancellationreason')}
          type={type}
          variant="outlined"
          error={isFieldDirty && cancelationReason === ''}
          fullWidth
        />
      );
    } else if (fieldKey === 'remark') {
      CustomTextField = (
        <TextField
          label={LabeledWithAsterisk(label, false)}
          value={remark}
          slotProps={{
            inputLabel: { sx: { color: '#333' } },
          }}
          variant="outlined"
          rows={rows}
          onChange={(e) => handleChange(e.target.value, 'remark')}
          // error={isFieldDirty && remark === ''}
          fullWidth
          multiline
        />
      );
    } else {
      CustomTextField = (
        <TextField
          label={LabeledWithAsterisk(label, true)}
          value={value}
          type={type}
          variant="outlined"
          rows={rows}
          disabled={isDisabled}
          fullWidth
          multiline
        />
      );
    }
    return (
      <Grid2
        size={{ xs: 12, sm: 6, md: width ?? 4 }}
        justifyContent="space-between"
      >
        {CustomTextField}
      </Grid2>
    );
  };

  // Localized department display
  const getDepartmentDisplayName = (departmentName: string) => {
    const nameMap: { [key: string]: string } = {
      'Admin': tCalendar('admin'),
      'Pathology': tCalendar('pathology'),
      'Semen Collection': tCalendar('semenCollection'),
      'Consultation': tCalendar('consultation'),
      'Ultrasound': tCalendar('ultrasound'),
      'Surgery': tCalendar('surgery')
    };
    return nameMap[departmentName] || departmentName;
  };

  return (
    <Box>
      <Divider
        style={{
          height: '1px',
          background: '#595959',
          marginTop: 20,
          marginBottom: 20,
        }}
      />

      <Grid2 container spacing={3} mt={2}>
        {renderDetail(tAppointmentForm('firstNameLabel'), data?.patient?.first_name, true)}
        {renderDetail(tAppointmentForm('lastNameLabel'), data?.patient?.last_name, true)}
        {renderDetail(tAppointmentForm('sexAssignedAtBirthLabel'), toTitleCase(data?.patient?.gender ?? ''), true)}
        {renderDetail(tAppointmentForm('departmentLabel'), getDepartmentDisplayName(data?.department__name || ''), true)}
        {renderDetail(
          (() => {
            if (data?.department__name === 'Admin') return tAppointmentForm('personnelLabel');
            if (data?.department__name === 'Pathology') return tAppointmentForm('pathologistLabel');
            return tAppointmentForm('doctorNameLabel');
          })(),
          `${data?.user?.first_name ?? ''} ${data?.user?.last_name ?? ''}`.trim(),
          true
        )}
        {renderDetail(
          tAppointmentForm('previousTimeLabel'),
          `${convertISOToTime(data?.start_datetime ?? '', true)}`,
          true,
          'time'
        )}
        {renderDetail(
          tAppointmentForm('appointmentReasonLabel'),
          data?.appointment_reason__reason__label ?? '',
          true,
          '',
          12
        )}
        {renderDetail(tAppointmentForm('cancellationReasonLabel'), '', false, '', 12, undefined, 'cancellationreason')}
        {renderDetail(tAppointmentForm('remarkLabel'), '', false, '', 12, 3, 'remark')}
      </Grid2>

      <Stack direction="row" mt={3} gap={3}>
        <Button
          fullWidth
          sx={{
            color: '#232323',
            borderRadius: 3,
            borderColor: '#00000',
            borderWidth: 2,
            fontWeight: 700,
            fontFamily: 'Montserrat',
            fontSize: '16px',
          }}
          variant="outlined"
          onClick={() => onClose(false)}
        >
          {tAppointmentForm('keepItButton')}
        </Button>
        <Button
          fullWidth
          sx={{
            color: '#FFFFFF',
            borderRadius: 3,
            borderWidth: 2,
            fontWeight: 700,
            fontFamily: 'Montserrat',
            fontSize: '16px',
            background: '#000000',
          }}
          variant="outlined"
          onClick={handleCancel}
        >
          {tAppointmentForm('confirmCancelButton')}
        </Button>
      </Stack>
    </Box>
  );
};

export default Cancel;